import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Menu, DoorOpen, User2, ExternalLink, Play, Copy, LayoutDashboard, CircleDot, BellDot } from 'lucide-react';
import { Button } from "./ui/button";
import { useAuth } from '../contexts/AuthContext';
import { LoginModal } from './LoginModal';
import { toast } from 'sonner';
import styles from '../styles/AppLayout.module.css';

interface AppLayoutProps extends React.PropsWithChildren {
  children?: React.ReactNode;
}

const PROTECTED_ROUTES = ['/outsourced-shows', '/all-shows', '/publish', '/floating-icons', '/tab-nudges'];

export function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, signOut, isAuth, loading } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Redirect unauthenticated users away from protected routes
  useEffect(() => {
    const isProtectedRoute = PROTECTED_ROUTES.some(route => location.pathname === route || location.pathname.startsWith(`${route}/`));

    if (!loading && !isAuth && isProtectedRoute) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuth, loading, location.pathname, navigate]);

  const handleSignIn = () => {
    setShowLoginModal(true);
  };

  const handleNavigateHome = () => {
    navigate('/dashboard');
  };

  const handleNavigateOutsourcedShows = () => {
    navigate('/outsourced-shows');
  };

  const handleNavigateAllShows = () => {
    navigate('/all-shows');
  };

  const handleNavigateFloatingIcons = () => {
    navigate('/floating-icons');
  };

  const handleNavigateTabNudges = () => {
    navigate('/tab-nudges');
  };

  const handleNavigateDashboard = () => {
    navigate('/dashboard');
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const handleCopyUID = async () => {
    if (user?.uid) {
      try {
        await navigator.clipboard.writeText(user.uid);
        toast.success('UID copied to clipboard');
      } catch (error) {
        toast.error('Failed to copy UID');
      }
    }
  };

  return (
    <div className={styles.layout}>
      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${isSidebarCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded}`}>
        {/* Company Name and Toggle */}
        <div className={styles.sidebarHeader}>
          {!isSidebarCollapsed && (
            <button
              type="button"
              className={styles.companyName}
              onClick={handleNavigateHome}
            >
              Dashverse CMS
            </button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className={styles.toggleButton}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
          >
            <Menu className="h-4 w-4" />
          </Button>
        </div>

        <div className={styles.sidebarContent}>
          {/* Action Buttons */}
          <div className={styles.newProjectSection}>
            <Button
              variant="secondary"
              className={`${styles.newProjectButton} ${isSidebarCollapsed ? styles.newProjectButtonCollapsed : styles.newProjectButtonExpanded} hidden`}
              onClick={handleNavigateHome}
            >
              <Plus className={styles.newProjectIcon} />
              {!isSidebarCollapsed && <span className={styles.newProjectText}>New Project</span>}
            </Button>
            {/*<Button
              variant="outline"
              className={`${styles.newProjectButton} ${isSidebarCollapsed ? styles.newProjectButtonCollapsed : styles.newProjectButtonExpanded} mt-2`}
              onClick={handleNavigateOutsourcedShows}
            >
              <ExternalLink className={styles.newProjectIcon} />
              {!isSidebarCollapsed && <span className={styles.newProjectText}>Outsourced Shows</span>}
            </Button>*/}
            {isAuth && (
              <>
                <Button
                  variant="outline"
                  className={`${styles.newProjectButton} ${isSidebarCollapsed ? styles.newProjectButtonCollapsed : styles.newProjectButtonExpanded} mt-2`}
                  onClick={handleNavigateDashboard}
                >
                  <LayoutDashboard className={styles.newProjectIcon} />
                  {!isSidebarCollapsed && <span className={styles.newProjectText}>Dashboard</span>}
                </Button>
                <Button
                  variant="outline"
                  className={`${styles.newProjectButton} ${isSidebarCollapsed ? styles.newProjectButtonCollapsed : styles.newProjectButtonExpanded} mt-2`}
                  onClick={handleNavigateAllShows}
                >
                  <Play className={styles.newProjectIcon} />
                  {!isSidebarCollapsed && <span className={styles.newProjectText}>All Shows</span>}
                </Button>
                <Button
                  variant="outline"
                  className={`${styles.newProjectButton} ${isSidebarCollapsed ? styles.newProjectButtonCollapsed : styles.newProjectButtonExpanded} mt-2`}
                  onClick={handleNavigateFloatingIcons}
                >
                  <CircleDot className={styles.newProjectIcon} />
                  {!isSidebarCollapsed && <span className={styles.newProjectText}>Floating Icons</span>}
                </Button>
                <Button
                  variant="outline"
                  className={`${styles.newProjectButton} ${isSidebarCollapsed ? styles.newProjectButtonCollapsed : styles.newProjectButtonExpanded} mt-2`}
                  onClick={handleNavigateTabNudges}
                >
                  <BellDot className={styles.newProjectIcon} />
                  {!isSidebarCollapsed && <span className={styles.newProjectText}>Tab Nudges</span>}
                </Button>
              </>
            )}
          </div>

          {/* Bottom Navigation */}
          <div className="mt-auto border-t p-2">
            {/* User Profile / Sign In */}
            <div className={`${isSidebarCollapsed ? 'px-2' : 'px-3'} py-2 mt-0.5 flex items-center justify-between bg-secondary/20 rounded-md`}>
              {user ? (
                <>
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-secondary/80 flex items-center justify-center text-secondary-foreground shrink-0">
                      {profile?.avatar_url ? (
                        <img src={profile.avatar_url} alt={profile.full_name || ''} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <span className="text-sm font-medium">
                          {profile?.full_name?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
                        </span>
                      )}
                    </div>
                    {!isSidebarCollapsed && (
                      <div className="ml-2 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {profile?.full_name || user?.email || 'Anonymous User'}
                        </div>
                        <div className="text-xs text-muted-foreground capitalize truncate flex items-center justify-between">
                          <span>{profile?.plan || 'Free'} Plan</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleCopyUID}
                            className="h-4 w-4 p-0 hover:bg-secondary/60"
                            title="Copy UID"
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  {!isSidebarCollapsed && (
                    <Button
                      variant="ghost"
                      onClick={handleSignOut}
                      className="h-8 w-8 p-0 ml-2 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      title="Logout"
                    >
                      <DoorOpen className="h-4 w-4" />
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <div className="w-8 h-8 rounded-full bg-secondary/80 flex items-center justify-center text-secondary-foreground">
                    <User2 className="h-4 w-4" />
                  </div>
                  {!isSidebarCollapsed ? (
                    <div className="ml-2 flex-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleSignIn}
                        className="w-full"
                      >
                        Sign in
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleSignIn}
                      className="h-8 w-8"
                    >
                      <User2 className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={styles.mainContent}>
        {children}
      </main>

      {/* Login Modal */}
      <LoginModal 
        open={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
      />
    </div>
  );
}
